import {
  CreateClaimForMemberRequest,
  CreateClaimNoteRequest,
  ReviewPendingClaimRequest,
  UpdateClaimRequest,
  UploadClaimAttachmentBody,
} from "@givenwell/management-api";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { api } from "./utils";

type SearchClaimsRequest = Parameters<typeof api.claims.searchClaims>[0];
type SearchClaimsStatus = SearchClaimsRequest["claimStatus"];

export const claimsListQueryKey = ["claims", "list"];

export const searchClaimsQuery = ({
  page,
  pageSize,
  memberName,
  friendlyId,
  vendorName,
  claimStatus,
  from,
  to,
  memberIds,
}: {
  page: number;
  pageSize: number;
  memberName?: string;
  friendlyId?: string;
  vendorName?: string;
  claimStatus?: SearchClaimsStatus;
  from?: Date;
  to?: Date;
  memberIds?: string[];
}) => {
  const cleanFrom =
    from === undefined ? undefined : new Date(from.getFullYear(), from.getMonth(), from.getDate()).toISOString();
  const cleanTo =
    to === undefined
      ? undefined
      : new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 9999).toISOString();
  const request: SearchClaimsRequest = {
    page,
    pageSize,
    memberName,
    friendlyId,
    vendorName,
    claimStatus,
    startDate: cleanFrom,
    endDate: cleanTo,
    memberIds,
  };
  return queryOptions({
    queryKey: [...claimsListQueryKey, request],
    queryFn: async () => {
      return await api.claims.searchClaims(request);
    },
  });
};

export const claimQuery = (claimId: string) =>
  queryOptions({
    queryKey: ["claims", claimId],
    queryFn: async () => {
      return await api.claims.getClaim({ claimId });
    },
  });

export function useUpdateClaimMutation() {
  return useMutation({
    mutationFn: async ({ claim_id, ...requestBody }: UpdateClaimRequest & { claim_id: string }) => {
      return await api.claims.updateClaim({ claimId: claim_id, requestBody: requestBody });
    },
  });
}

export function useReviewPendingClaimMutation() {
  return useMutation({
    mutationFn: async ({ claim_id, ...requestBody }: ReviewPendingClaimRequest & { claim_id: string }) => {
      return await api.claims.reviewPendingClaim({ claimId: claim_id, requestBody: requestBody });
    },
  });
}

export type CreateClaimNoteOptions = CreateClaimNoteRequest & { claim_id: string };
export function useCreateClaimNoteMutation() {
  return useMutation({
    mutationFn: async ({ claim_id, ...body }: CreateClaimNoteOptions) => {
      const res = await api.claims.createClaimNote({
        claimId: claim_id,
        requestBody: body,
      });
      return res;
    },
  });
}

export function useUploadClaimAttachmentForMemberMutation() {
  return useMutation({
    mutationFn: async ({ memberId, file }: { memberId: string; file: File }) => {
      const body: UploadClaimAttachmentBody = {
        filename: file.name,
        mime_type: file.type,
      };
      const res = await api.claims.uploadClaimAttachmentForMember({ memberId, requestBody: body });
      await fetch(res.upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      return res;
    },
  });
}

export function useCreateClaimForMemberMutation() {
  return useMutation({
    mutationFn: async (request: CreateClaimForMemberRequest) => {
      return await api.claims.createClaimForMember({ requestBody: request });
    },
  });
}

export function usePayoutClaimMutation() {
  return useMutation({
    mutationFn: async ({ claim_id }: { claim_id: string }) => {
      return await api.claims.payoutClaim({ claimId: claim_id });
    },
  });
}
